import React, { useEffect, useState } from "react";
import api from "../api";
import { formatCurrency } from "../utils/formatCurrency";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from "recharts";

const MONTH_LABELS = ["jan", "fév", "mar", "avr", "mai", "jun", "jul", "aoû", "sep", "oct", "nov", "déc"];

function monthLabel(key) {
  const [y, m] = key.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

function cellColor(pct, method) {
  if (method === "none") return null;
  if (pct === null) return null;
  if (pct >= 100) return "var(--danger)";
  if (pct >= 80)  return "var(--warning)";
  return "var(--success)";
}

function CellContent({ d, method }) {
  if (method === "none") return <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>;

  const spent  = d?.spent  ?? 0;
  const budget = d?.budget ?? 0;
  const pct    = budget > 0 ? Math.round((spent / budget) * 100) : null;
  const color  = cellColor(pct, method);

  if (spent === 0 && budget === 0) {
    return <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>;
  }

  return (
    <div style={{ textAlign: "right" }}>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{formatCurrency(spent)}</div>
      {pct !== null && (
        <div style={{ fontSize: 11, color, fontWeight: 500 }}>{pct} %</div>
      )}
      {budget > 0 && (
        <div style={{ marginTop: 3, height: 3, borderRadius: 2, background: "#e8e8e8", overflow: "hidden" }}>
          <div style={{ width: `${Math.min(pct ?? 0, 100)}%`, height: "100%", background: color || "#bbb", borderRadius: 2 }} />
        </div>
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8,
      padding: "10px 14px", fontSize: 13, boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: "var(--text)" }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 20, color: p.color }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function TrendsTable({ month }) {
  const [data, setData]         = useState(null);
  const [nbMonths, setNbMonths] = useState(6);
  const [collapsed, setCollapsed] = useState(new Set());
  const [selectedKey, setSelectedKey] = useState(""); // "" = total, "cat:X" = catégorie, "sub:N" = sous-cat
  const [view, setView] = useState("both"); // "chart" | "table" | "both"

  useEffect(() => {
    setData(null);
    api.get(`/budget/trends?month=${month}&months=${nbMonths}`)
      .then(res => {
        setData(res.data);
        const cats = [...new Set(res.data.rows.map(r => r.category_name))];
        setCollapsed(new Set(cats));
      })
      .catch(err => console.error("trends error:", err));
  }, [month, nbMonths]);

  if (!data) return <div style={{ color: "var(--text-soft)", padding: 24 }}>Chargement…</div>;

  const { rows } = data;
  const subVacMap = data.subVacationMonths || {};
  const months = [...data.months].reverse(); // chronologique : ancien → récent
  const allCats = [...new Set(rows.map(r => r.category_name))];
  const allCollapsed = allCats.every(c => collapsed.has(c));

  const toggleCat = (cat) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const grouped = allCats.map(cat => ({
    cat,
    items: rows.filter(r => r.category_name === cat),
  }));

  // ── Données graphique ──────────────────────────────────────────────────────
  function getChartData() {
    // Set des mois vacances selon la sélection courante
    let vacSet;
    if (!selectedKey) {
      vacSet = new Set(data.vacationMonths || []);
    } else if (selectedKey.startsWith("cat:")) {
      const cat = selectedKey.slice(4);
      const ids = rows.filter(r => r.category_name === cat).map(r => r.subcategory_id);
      vacSet = new Set(ids.flatMap(id => subVacMap[id] || []));
    } else {
      const id = Number(selectedKey.slice(4));
      vacSet = new Set(subVacMap[id] || []);
    }

    return months.map(mk => {
      let spent, budget;
      if (!selectedKey) {
        spent  = rows.reduce((s, r) => s + (r.months[mk]?.spent  ?? 0), 0);
        budget = rows.reduce((s, r) => s + (r.months[mk]?.budget ?? 0), 0);
      } else if (selectedKey.startsWith("cat:")) {
        const cat = selectedKey.slice(4);
        const catRows = rows.filter(r => r.category_name === cat);
        spent  = catRows.reduce((s, r) => s + (r.months[mk]?.spent  ?? 0), 0);
        budget = catRows.reduce((s, r) => s + (r.months[mk]?.budget ?? 0), 0);
      } else {
        const id = Number(selectedKey.slice(4));
        const row = rows.find(r => r.subcategory_id === id);
        spent  = row?.months[mk]?.spent  ?? 0;
        budget = row?.months[mk]?.budget ?? 0;
      }
      return { month: monthLabel(mk), spent, budget, isVacation: vacSet.has(mk) };
    });
  }

  function getChartTitle() {
    if (!selectedKey) return "Total toutes catégories";
    if (selectedKey.startsWith("cat:")) return selectedKey.slice(4);
    const id = Number(selectedKey.slice(4));
    return rows.find(r => r.subcategory_id === id)?.subcategory_name || "";
  }

  const chartData = getChartData();
  const hasBudget = chartData.some(d => d.budget > 0);

  const thStyle = {
    textAlign: "right", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap",
    padding: "10px 12px", borderBottom: "2px solid var(--border)",
    background: "var(--surface)", position: "sticky", top: 0,
  };
  const tdStyle = {
    padding: "8px 12px", borderBottom: "1px solid var(--border)",
    verticalAlign: "middle", minWidth: 90,
  };

  const periodBtns = [3, 6, 12];

  return (
    <div>

      {/* Contrôles */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        <span style={{ fontSize: 13, color: "var(--text-soft)" }}>Période :</span>
        {periodBtns.map(n => (
          <button key={n} onClick={() => setNbMonths(n)} style={{
            padding: "5px 14px", borderRadius: 6, border: "1px solid var(--border)",
            background: nbMonths === n ? "var(--primary)" : "var(--surface)",
            color: nbMonths === n ? "#fff" : "var(--text-soft)",
            fontWeight: nbMonths === n ? 600 : 400, cursor: "pointer", fontSize: 13,
          }}>{n} mois</button>
        ))}

        <div style={{ width: 1, height: 24, background: "var(--border)", margin: "0 4px" }} />

        <span style={{ fontSize: 13, color: "var(--text-soft)" }}>Vue :</span>
        {[["both", "Graphique + Tableau"], ["chart", "Graphique"], ["table", "Tableau"]].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)} style={{
            padding: "5px 12px", borderRadius: 6, border: "1px solid var(--border)",
            background: view === id ? "var(--primary)" : "var(--surface)",
            color: view === id ? "#fff" : "var(--text-soft)",
            fontWeight: view === id ? 600 : 400, cursor: "pointer", fontSize: 13,
          }}>{label}</button>
        ))}

        {view !== "table" && (
          <>
            <div style={{ width: 1, height: 24, background: "var(--border)", margin: "0 4px" }} />
            <select
              value={selectedKey}
              onChange={e => setSelectedKey(e.target.value)}
              style={{ minWidth: 200, fontSize: 13 }}
            >
              <option value="">Total général</option>
              {grouped.map(({ cat, items }) => (
                <optgroup key={cat} label={cat}>
                  <option value={`cat:${cat}`}>{cat} (total)</option>
                  {items.map(r => (
                    <option key={r.subcategory_id} value={`sub:${r.subcategory_id}`}>
                      {r.subcategory_name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </>
        )}

        {view !== "chart" && (
          <button
            onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(allCats))}
            style={{ marginLeft: "auto", fontSize: 12, padding: "4px 10px", cursor: "pointer", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text-soft)" }}
          >
            {allCollapsed ? "Tout développer" : "Tout réduire"}
          </button>
        )}
      </div>

      {/* ── GRAPHIQUE ─────────────────────────────────────────────────────── */}
      {view !== "table" && (
        <div style={{
          position: "sticky", top: 0, zIndex: 10,
          background: "var(--bg)", paddingBottom: view === "both" ? 16 : 0,
        }}>
        <div style={{
          background: "var(--card)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)", padding: "20px 16px 12px",
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, color: "var(--text)" }}>
            {getChartTitle()}
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: "var(--text-muted)" }}
                axisLine={false} tickLine={false}
              />
              <YAxis
                tickFormatter={v => `${Math.round(v / 1000) > 0 ? `${(v/1000).toFixed(1)}k` : v}`}
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                axisLine={false} tickLine={false} width={48}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 13, paddingTop: 12 }} />
              {hasBudget && (
                <Line
                  type="monotone" dataKey="budget" name="Budget"
                  stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="6 3"
                  dot={false} activeDot={{ r: 4 }}
                />
              )}
              <Line
                type="monotone" dataKey="spent" name="Dépensé"
                stroke="var(--primary)" strokeWidth={2.5}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload.isVacation) {
                    return (
                      <g key={`dot-${cx}-${cy}`}>
                        <circle cx={cx} cy={cy} r={8} fill="#f59e0b" fillOpacity={0.2} />
                        <circle cx={cx} cy={cy} r={5} fill="#f59e0b" stroke="#fff" strokeWidth={1.5} />
                        <text x={cx} y={cy - 14} textAnchor="middle" fontSize={10} fontWeight={600} fill="#b45309" letterSpacing={0.3}>
                          vacances
                        </text>
                      </g>
                    );
                  }
                  return <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={3} fill="var(--primary)" />;
                }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        </div>
      )}

      {/* ── TABLEAU ───────────────────────────────────────────────────────── */}
      {view !== "chart" && (
        <>
          <div style={{ display: "flex", gap: 16, marginBottom: 14, fontSize: 12, color: "var(--text-muted)" }}>
            <span><span style={{ color: "var(--success)", fontWeight: 700 }}>●</span> &lt; 80 %</span>
            <span><span style={{ color: "var(--warning)", fontWeight: 700 }}>●</span> 80–99 %</span>
            <span><span style={{ color: "var(--danger)", fontWeight: 700 }}>●</span> ≥ 100 %</span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 500 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left", minWidth: 180, position: "sticky", left: 0, zIndex: 2 }}>
                    Sous-catégorie
                  </th>
                  {months.map(m => (
                    <th key={m} style={thStyle}>{monthLabel(m)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map(({ cat, items }) => {
                  const isCollapsed = collapsed.has(cat);
                  const catMonthTotals = months.map(mk => {
                    const spent  = items.reduce((s, r) => s + (r.months[mk]?.spent  ?? 0), 0);
                    const budget = items.reduce((s, r) => s + (r.months[mk]?.budget ?? 0), 0);
                    const pct    = budget > 0 ? Math.round((spent / budget) * 100) : null;
                    return { mk, spent, budget, pct };
                  });

                  return (
                    <React.Fragment key={cat}>
                      <tr onClick={() => toggleCat(cat)} style={{ cursor: "pointer", background: "var(--surface-alt, #f5f5f5)", userSelect: "none" }}>
                        <td style={{ ...tdStyle, fontWeight: 700, fontSize: 13, position: "sticky", left: 0, background: "var(--surface-alt, #f5f5f5)", zIndex: 1 }}>
                          <span style={{ fontSize: 11, color: "var(--text-muted)", marginRight: 8 }}>{isCollapsed ? "▸" : "▾"}</span>
                          {cat}
                          {isCollapsed && (
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: "var(--text-muted)" }}>
                              {items.length} sous-catégorie{items.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </td>
                        {catMonthTotals.map(({ mk, spent, budget, pct }) => {
                          const color = budget > 0
                            ? (pct >= 100 ? "var(--danger)" : pct >= 80 ? "var(--warning)" : "var(--success)")
                            : "var(--text-muted)";
                          return (
                            <td key={mk} style={{ ...tdStyle, background: "var(--surface-alt, #f5f5f5)", textAlign: "right" }}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{spent > 0 ? formatCurrency(spent) : "—"}</div>
                              {isCollapsed && pct !== null && (
                                <div style={{ fontSize: 11, color, fontWeight: 500 }}>{pct} %</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {!isCollapsed && items.map(row => (
                        <tr
                          key={row.subcategory_id}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedKey(`sub:${row.subcategory_id}`);
                            if (view === "table") setView("both");
                          }}
                        >
                          <td style={{ ...tdStyle, paddingLeft: 28, color: "var(--text-soft)", fontSize: 13, position: "sticky", left: 0, background: "var(--card, #fff)", zIndex: 1 }}>
                            {row.subcategory_name}
                          </td>
                          {months.map(mk => (
                            <td key={mk} style={{ ...tdStyle }}>
                              <CellContent d={row.months[mk]} method={row.budget_method} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
